import { useCallback, useState } from 'react';
import { HURRICANES_ALL_LIVE_STORMS_KEY, HURRICANES_DATASETS } from '../config/hurricanes.config';

// Owns which dataset (live/historical) and which storm is selected.
// Selection is remembered per dataset, and the live dataset automatically
// falls back to historical when there are no active storms.
export default function useStormSelection({
  liveStormGroups = [],
  historicalStormGroups = [],
  hasLiveStorms = false,
}) {
  const [selectedDataset, setSelectedDataset] = useState(HURRICANES_DATASETS.live);
  const [selectedKeysByDataset, setSelectedKeysByDataset] = useState({});

  const dataset =
    selectedDataset === HURRICANES_DATASETS.live && !hasLiveStorms
      ? HURRICANES_DATASETS.historical
      : selectedDataset;
  const stormGroups =
    dataset === HURRICANES_DATASETS.live ? liveStormGroups : historicalStormGroups;

  const defaultStormKey =
    dataset === HURRICANES_DATASETS.live
      ? HURRICANES_ALL_LIVE_STORMS_KEY
      : (stormGroups[0]?.key ?? null);
  const rememberedStormKey = selectedKeysByDataset[dataset];
  const isRememberedStormAvailable =
    (dataset === HURRICANES_DATASETS.live &&
      rememberedStormKey === HURRICANES_ALL_LIVE_STORMS_KEY) ||
    stormGroups.some((storm) => storm.key === rememberedStormKey);
  const selectedStormKey = isRememberedStormAvailable ? rememberedStormKey : defaultStormKey;
  const selectedStorm = stormGroups.find((storm) => storm.key === selectedStormKey) ?? null;
  // "All Live Storms" keeps every storm on the map; a selection narrows to one.
  const visibleStormGroups = selectedStorm ? [selectedStorm] : stormGroups;

  const selectStorm = useCallback(
    (stormKey) => setSelectedKeysByDataset((previous) => ({ ...previous, [dataset]: stormKey })),
    [dataset],
  );

  const setDatasetToHistorical = useCallback(
    (isHistorical) =>
      setSelectedDataset(isHistorical ? HURRICANES_DATASETS.historical : HURRICANES_DATASETS.live),
    [],
  );

  return {
    dataset,
    stormGroups,
    selectedStormKey,
    selectedStorm,
    visibleStormGroups,
    selectStorm,
    setDatasetToHistorical,
  };
}
